import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './operations.css';

const API_BASE_URL =
  process.env.REACT_APP_EP || 'http://localhost:5000/api';

// Map between React column keys and DB segment values
const SEGMENT_KEY_TO_DB = {
  stocks: 'Stocks',
  office: 'Office',
  printing: 'Printing',
  binding: 'Binding',
  stitching: 'Stitching',
};
const SEGMENT_DB_TO_KEY = Object.fromEntries(
  Object.entries(SEGMENT_KEY_TO_DB).map(([k, v]) => [v, k])
);

// DB → UI
// DB → UI
const mapTaskFromApi = (t) => {
  const imageUrl = t.image_url || '';
  return {
    id: t.id,
    segment: t.segment,
    title: t.title,
    quantity: t.quantity ?? '',
    assignee: t.assignee ?? '',
    startDate: normalizeDate(t.start_date),
    endDate: normalizeDate(t.end_date),
    note: t.note || '',
    completed: Boolean(t.completed),
    completedAt: t.completed_at || null,
    progress: t.progress ?? 0,
    imageUrl,              // URL from backend
    imagePreview: imageUrl // use URL for card preview
  };
};

// UI → DB payload
// UI → plain payload (no file yet)
const mapTaskToApi = (columnKey, data, overrides = {}) => ({
  segment: SEGMENT_KEY_TO_DB[columnKey],
  title: data.title,
  quantity: data.quantity ? Number(data.quantity) : null,
  assignee: data.assignee || null,
  start_date: data.startDate || null,
  end_date: data.endDate || null,
  note: data.note || null,
  completed: overrides.completed ?? data.completed ?? false,
  completed_at: overrides.completedAt ?? data.completedAt ?? null,
});

const buildFormData = (columnKey, data, overrides = {}) => {
  const payload = mapTaskToApi(columnKey, data, overrides);
  const fd = new FormData();

  // Append only defined / non-empty scalar fields
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      fd.append(key, value);
    }
  });

  // Append image file if user selected one
  if (data.imageFile) {
    fd.append('image', data.imageFile); // field name must be "image"
  }

  return fd;
};

// API functions
async function apiFetchTasks() {
  const res = await fetch(`${API_BASE_URL}/api/operation/`);
  if (!res.ok) throw new Error('Failed to load tasks');
  const data = await res.json();
  return data.map(mapTaskFromApi);
}

async function apiCreateTask(columnKey, formState) {
  const fd = buildFormData(columnKey, formState);

  const res = await fetch(`${API_BASE_URL}/api/operation`, {
    method: 'POST',
    body: fd, // NO Content-Type header; browser sets multipart/form-data
  });

  if (!res.ok) throw new Error('Failed to create task');
  const json = await res.json();
  return mapTaskFromApi(json);
}

async function apiUpdateTask(columnKey, id, taskData, overrides = {}) {
  const fd = buildFormData(columnKey, taskData, overrides);

  const res = await fetch(`${API_BASE_URL}/api/operation/${id}`, {
    method: 'PUT',
    body: fd,
  });

  if (!res.ok) throw new Error('Failed to update task');
  const json = await res.json();
  return mapTaskFromApi(json);
}

async function apiDeleteTask(id) {
  const res = await fetch(`${API_BASE_URL}/api/operation/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete task');
}


// SVG Icons
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
);
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
);
const NoteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
);
const CheckCircleIcon = ({ completed }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={completed ? "#4caf50" : "none"} stroke={completed ? "#4caf50" : "#ccc"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
);
const ChevronDownIcon = ({ isOpen }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
);
const StatsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"></path><path d="M21 5c0 1.66-4 3-9 3S3 6.66 3 5s4-3 9-3 9 1.34 9 3Z"></path></svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
);
const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
);

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Normalize API date strings to "YYYY-MM-DD"
const normalizeDate = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    // Handles "2026-01-27T18:30:00.000Z" or "2026-01-27 18:30:00"
    const [datePart] = value.split('T');
    return datePart.split(' ')[0]; // in case it's "YYYY-MM-DD HH:MM:SS"
  }

  try {
    // Fallback for Date objects or other formats
    return new Date(value).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const Operations = () => {
  const [columns, setColumns] = useState({
    stocks: { title: 'Stocks', color: '#4c63b6', items: [] },
    office: { title: 'Office', color: '#4c63b6', items: [] },
    printing: { title: 'Printing', color: '#4c63b6', items: [] },
    binding: { title: 'Binding', color: '#4c63b6', items: [] },
    stitching: { title: 'Stitching', color: '#4c63b6', items: [] },
  });


  const [expandedCompleted, setExpandedCompleted] = useState({
    stocks: false,
    office: false,
    printing: false,
    binding: false,
    stitching: false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeColumnKey, setActiveColumnKey] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    quantity: '',
    assignee: '',
    startDate: '',
    endDate: '',
    note: '',
    imageFile: null,      // new
    imagePreview: '',     // new (base64 or object URL for preview)
  });

  // ✅ NEW: Date Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
    isActive: false
  });
  const filterRef = useRef(null);

  // Stats Panel State
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [statsFilter, setStatsFilter] = useState('all');
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  // Drag State
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedItem: null,
    sourceColumnKey: null,
    sourceIndex: null,
    targetIndex: null,
  });

  const dragRef = useRef({
    element: null,
    clone: null,
    placeholder: null,
    columnContent: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    scrollInterval: null,
    hasMoved: false,
    startTime: 0,
  });

  const containerRef = useRef(null);

  useEffect(() => {
    const loadTasks = async () => {
      try {
        const tasks = await apiFetchTasks();

        // Start from empty columns
        const nextColumns = {
          stocks: { title: 'Stocks', color: '#4c63b6', items: [] },
          office: { title: 'Office', color: '#4c63b6', items: [] },
          printing: { title: 'Printing', color: '#4c63b6', items: [] },
          binding: { title: 'Binding', color: '#4c63b6', items: [] },
          stitching: { title: 'Stitching', color: '#4c63b6', items: [] },
        };

        tasks.forEach((task) => {
          const key = SEGMENT_DB_TO_KEY[task.segment];
          if (key && nextColumns[key]) {
            nextColumns[key].items.push(task);
          }
        });

        setColumns(nextColumns);
      } catch (err) {
        console.error(err);
        alert('Failed to load tasks from server');
      }
    };

    loadTasks();
  }, []);

  //state for image viewer modal
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    src: '',
    title: '',
  });

  const openImageViewer = (src, title = '') => {
    if (!src) return;
    setImageViewer({
      isOpen: true,
      src,
      title,
    });
  };

  const closeImageViewer = () => {
    setImageViewer(prev => ({ ...prev, isOpen: false }));
  };

  // Optional: close on ESC and lock scroll when open
  useEffect(() => {
    if (!imageViewer.isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeImageViewer();
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [imageViewer.isOpen]);

  // ✅ NEW: Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ NEW: Apply date filter to columns
  const filteredColumns = useMemo(() => {
    if (!dateFilter.isActive) {
      return columns;
    }

    const filtered = {};
    Object.entries(columns).forEach(([key, column]) => {
      const filteredItems = column.items.filter(item => {
        if (!item.startDate) return true; // Show items without dates

        const itemDate = new Date(item.startDate);
        const filterStart = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
        const filterEnd = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

        if (filterStart && filterEnd) {
          return itemDate >= filterStart && itemDate <= filterEnd;
        } else if (filterStart) {
          return itemDate >= filterStart;
        } else if (filterEnd) {
          return itemDate <= filterEnd;
        }
        return true;
      });

      filtered[key] = {
        ...column,
        items: filteredItems
      };
    });

    return filtered;
  }, [columns, dateFilter]);

  // ✅ NEW: Apply search over date-filtered columns
  const searchFilteredColumns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filteredColumns;

    const result = {};

    Object.entries(filteredColumns).forEach(([key, column]) => {
      const matchedItems = column.items.filter((item) => {
        const title = (item.title || '').toLowerCase();
        const assignee = (item.assignee || '').toLowerCase();
        const note = (item.note || '').toLowerCase();
        const quantity =
          item.quantity !== undefined && item.quantity !== null
            ? String(item.quantity).toLowerCase()
            : '';

        return (
          title.includes(q) ||
          assignee.includes(q) ||
          note.includes(q) ||
          quantity.includes(q)
        );
      });

      // Only keep columns that have at least one matching task
      if (matchedItems.length > 0) {
        result[key] = {
          ...column,
          items: matchedItems,
        };
      }
    });

    return result;
  }, [filteredColumns, searchQuery]);

  // ✅ NEW: Handle filter apply
  const handleApplyFilter = () => {
    if (dateFilter.startDate || dateFilter.endDate) {
      setDateFilter(prev => ({ ...prev, isActive: true }));
    }
    setIsFilterOpen(false);
  };

  // ✅ NEW: Handle filter clear
  const handleClearFilter = () => {
    setDateFilter({
      startDate: '',
      endDate: '',
      isActive: false
    });
    setIsFilterOpen(false);
  };

  // ============ STATS CALCULATION ============

  const assigneeStats = useMemo(() => {
    const stats = {};

    Object.entries(columns).forEach(([columnKey, column]) => {
      column.items.forEach(item => {
        if (item.completed) {
          const assigneeName = item.assignee || 'Unassigned';

          if (!stats[assigneeName]) {
            stats[assigneeName] = {
              name: assigneeName,
              totalCompleted: 0,
              totalQuantity: 0,
              tasks: [],
              byColumn: {}
            };
          }

          stats[assigneeName].totalCompleted += 1;
          stats[assigneeName].totalQuantity += parseInt(item.quantity) || 0;
          stats[assigneeName].tasks.push({
            ...item,
            columnKey,
            columnTitle: column.title
          });

          if (!stats[assigneeName].byColumn[columnKey]) {
            stats[assigneeName].byColumn[columnKey] = {
              columnTitle: column.title,
              count: 0,
              quantity: 0,
              tasks: []
            };
          }
          stats[assigneeName].byColumn[columnKey].count += 1;
          stats[assigneeName].byColumn[columnKey].quantity += parseInt(item.quantity) || 0;
          stats[assigneeName].byColumn[columnKey].tasks.push(item);
        }
      });
    });

    return Object.values(stats).sort((a, b) => b.totalCompleted - a.totalCompleted);
  }, [columns]);

  const filteredStats = useMemo(() => {
    if (statsFilter === 'all') {
      return assigneeStats;
    }

    return assigneeStats
      .map(assignee => {
        const columnData = assignee.byColumn[statsFilter];
        if (!columnData) return null;

        return {
          ...assignee,
          totalCompleted: columnData.count,
          totalQuantity: columnData.quantity,
          tasks: columnData.tasks.map(task => ({
            ...task,
            columnKey: statsFilter,
            columnTitle: columns[statsFilter].title
          }))
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.totalCompleted - a.totalCompleted);
  }, [assigneeStats, statsFilter, columns]);

  const totalStats = useMemo(() => {
    let total = 0;
    let totalQty = 0;

    if (statsFilter === 'all') {
      Object.values(columns).forEach(column => {
        column.items.forEach(item => {
          if (item.completed) {
            total += 1;
            totalQty += parseInt(item.quantity) || 0;
          }
        });
      });
    } else {
      columns[statsFilter].items.forEach(item => {
        if (item.completed) {
          total += 1;
          totalQty += parseInt(item.quantity) || 0;
        }
      });
    }

    return { total, totalQty };
  }, [columns, statsFilter]);

  const exportToCSV = () => {
    const headers = ['Assignee', 'Tasks Completed', 'Total Quantity', 'Task Details'];
    const rows = filteredStats.map(assignee => [
      assignee.name,
      assignee.totalCompleted,
      assignee.totalQuantity,
      assignee.tasks.map(t => `${t.title} (${t.columnTitle})`).join('; ')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `completed-tasks-by-assignee-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ============ TASK FUNCTIONS ============

  const getTasksByStatus = (items) => {
    const incomplete = items.filter(item => !item.completed);
    const completed = items.filter(item => item.completed);
    return { incomplete, completed };
  };

  const toggleCompletedSection = (columnKey) => {
    setExpandedCompleted(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const clearCompletedTasks = async (e, columnKey) => {
    e.stopPropagation();

    const completed = columns[columnKey].items.filter((item) => item.completed);
    if (completed.length === 0) return;

    if (!window.confirm('Are you sure you want to remove all completed tasks?')) {
      return;
    }

    try {
      await Promise.all(completed.map((t) => apiDeleteTask(t.id)));

      setColumns((prev) => ({
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          items: prev[columnKey].items.filter((item) => !item.completed),
        },
      }));
    } catch (err) {
      console.error(err);
      alert('Failed to clear some completed tasks');
    }
  };

  useEffect(() => {
    return () => {
      cleanupDrag();
    };
  }, []);

  const cleanupDrag = () => {
    const drag = dragRef.current;

    if (drag.clone && drag.clone.parentNode) {
      drag.clone.parentNode.removeChild(drag.clone);
    }
    if (drag.placeholder && drag.placeholder.parentNode) {
      drag.placeholder.parentNode.removeChild(drag.placeholder);
    }
    if (drag.scrollInterval) {
      clearInterval(drag.scrollInterval);
    }
    if (drag.element) {
      drag.element.classList.remove('task-board-card-hidden');
    }

    document.body.classList.remove('task-board-dragging');

    dragRef.current = {
      element: null,
      clone: null,
      placeholder: null,
      columnContent: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      scrollInterval: null,
      hasMoved: false,
      startTime: 0,
    };
  };

  const handleMouseDown = (e, columnKey, item, index) => {
    if (item.completed) return;

    if (e.target.closest('.task-board-complete-btn')) {
      return;
    }

    e.preventDefault();

    const cardElement = e.target.closest('.task-board-card');
    if (!cardElement) return;

    const rect = cardElement.getBoundingClientRect();
    const columnContent = cardElement.closest('.task-board-incomplete-section');

    if (!columnContent) return;

    dragRef.current.element = cardElement;
    dragRef.current.columnContent = columnContent;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.offsetX = e.clientX - rect.left;
    dragRef.current.offsetY = e.clientY - rect.top;
    dragRef.current.hasMoved = false;
    dragRef.current.startTime = Date.now();

    setDragState({
      isDragging: false,
      draggedItem: item,
      sourceColumnKey: columnKey,
      sourceIndex: index,
      targetIndex: index,
    });

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag.element) return;

    const deltaX = Math.abs(e.clientX - drag.startX);
    const deltaY = Math.abs(e.clientY - drag.startY);

    if (!drag.hasMoved && (deltaX > 5 || deltaY > 5)) {
      drag.hasMoved = true;
      initializeDrag(e);
    }

    if (!drag.clone) return;

    drag.clone.style.left = `${e.clientX - drag.offsetX}px`;
    drag.clone.style.top = `${e.clientY - drag.offsetY}px`;

    const columnContent = drag.columnContent;
    if (!columnContent) return;

    const cards = Array.from(
      columnContent.querySelectorAll('.task-board-card:not(.task-board-card-hidden)')
    );

    let targetIndex = 0;
    let insertBefore = null;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const cardRect = card.getBoundingClientRect();
      const cardMiddle = cardRect.top + cardRect.height / 2;

      if (e.clientY < cardMiddle) {
        insertBefore = card;
        targetIndex = i;
        break;
      }
      targetIndex = i + 1;
    }

    if (cards.length > 0 && !insertBefore) {
      const firstCardRect = cards[0].getBoundingClientRect();
      if (e.clientY < firstCardRect.top) {
        insertBefore = cards[0];
        targetIndex = 0;
      }
    }

    if (insertBefore) {
      if (drag.placeholder.nextSibling !== insertBefore) {
        columnContent.insertBefore(drag.placeholder, insertBefore);
      }
    } else {
      if (drag.placeholder.parentNode === columnContent && drag.placeholder.nextSibling !== null) {
        columnContent.appendChild(drag.placeholder);
      } else if (drag.placeholder.parentNode !== columnContent) {
        columnContent.appendChild(drag.placeholder);
      }
    }

    setDragState(prev => ({
      ...prev,
      targetIndex: targetIndex,
    }));

    handleAutoScroll(e, columnContent.closest('.task-board-column-content'));
  }, []);

  const initializeDrag = (e) => {
    const drag = dragRef.current;
    const cardElement = drag.element;
    const columnContent = drag.columnContent;

    if (!cardElement || !columnContent) return;

    const rect = cardElement.getBoundingClientRect();

    document.body.classList.add('task-board-dragging');

    const clone = cardElement.cloneNode(true);
    clone.classList.add('task-board-card-ghost');
    clone.classList.remove('task-board-card-hidden');
    clone.style.position = 'fixed';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    clone.style.margin = '0';
    document.body.appendChild(clone);
    drag.clone = clone;

    const placeholder = document.createElement('div');
    placeholder.classList.add('task-board-card-placeholder');
    placeholder.style.height = `${rect.height}px`;
    cardElement.parentNode.insertBefore(placeholder, cardElement);
    drag.placeholder = placeholder;

    cardElement.classList.add('task-board-card-hidden');

    setDragState(prev => ({
      ...prev,
      isDragging: true,
    }));
  };

  const handleAutoScroll = (e, container) => {
    if (!container) return;

    const drag = dragRef.current;
    const rect = container.getBoundingClientRect();
    const scrollSpeed = 8;
    const threshold = 60;

    if (drag.scrollInterval) {
      clearInterval(drag.scrollInterval);
      drag.scrollInterval = null;
    }

    if (e.clientY < rect.top + threshold && container.scrollTop > 0) {
      drag.scrollInterval = setInterval(() => {
        container.scrollTop -= scrollSpeed;
      }, 16);
    } else if (e.clientY > rect.bottom - threshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight) {
      drag.scrollInterval = setInterval(() => {
        container.scrollTop += scrollSpeed;
      }, 16);
    }
  };

  const handleMouseUp = useCallback((e) => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    const drag = dragRef.current;
    const timeDiff = Date.now() - drag.startTime;

    if (!drag.hasMoved || timeDiff < 150) {
      cleanupDrag();

      setDragState(prev => {
        if (prev.draggedItem && !drag.hasMoved) {
          setTimeout(() => {
            handleEditClick(prev.sourceColumnKey, prev.draggedItem);
          }, 0);
        }

        return {
          isDragging: false,
          draggedItem: null,
          sourceColumnKey: null,
          sourceIndex: null,
          targetIndex: null,
        };
      });
      return;
    }

    setDragState(prev => {
      if (prev.isDragging && prev.draggedItem && prev.sourceIndex !== prev.targetIndex) {
        setColumns(cols => {
          const newColumns = { ...cols };
          const columnKey = prev.sourceColumnKey;

          const allItems = [...newColumns[columnKey].items];
          const incompleteItems = allItems.filter(item => !item.completed);
          const completedItems = allItems.filter(item => item.completed);

          const [draggedItem] = incompleteItems.splice(prev.sourceIndex, 1);

          let adjustedTargetIndex = prev.targetIndex;
          if (prev.sourceIndex < prev.targetIndex) {
            adjustedTargetIndex = prev.targetIndex - 1;
          }

          incompleteItems.splice(adjustedTargetIndex, 0, draggedItem);

          newColumns[columnKey] = {
            ...newColumns[columnKey],
            items: [...incompleteItems, ...completedItems]
          };

          return newColumns;
        });
      }

      return {
        isDragging: false,
        draggedItem: null,
        sourceColumnKey: null,
        sourceIndex: null,
        targetIndex: null,
      };
    });

    cleanupDrag();
  }, [handleMouseMove]);

  // ✅ UPDATED: handleAddClick with today's date as default
  const handleAddClick = (columnKey) => {
    if (dragState.isDragging) return;
    setActiveColumnKey(columnKey);
    setEditingTaskId(null);
    const today = getTodayDate();
    setFormData({
      title: '',
      quantity: '',
      assignee: '',
      startDate: today,
      endDate: today,
      note: '',
      imageFile: null,
      imagePreview: '',
    });
    setIsModalOpen(true);
  };

  const handleEditClick = (columnKey, task) => {
    if (dragState.isDragging) return;

    setActiveColumnKey(columnKey);
    setEditingTaskId(task.id);
    setFormData({
      title: task.title,
      quantity: task.quantity,
      assignee: task.assignee,
      startDate: task.startDate,
      endDate: task.endDate,
      note: task.note || '',
      imageFile: null,
      imagePreview: task.imagePreview || '',   // new
    });
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFormData((prev) => ({
        ...prev,
        imageFile: null,
        imagePreview: '',
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result, // base64 data URL for preview only
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title) return alert('Title is required');
    if (!activeColumnKey) return;

    try {
      if (editingTaskId) {
        const existing =
          columns[activeColumnKey].items.find((i) => i.id === editingTaskId) || {};

        const updatedTask = await apiUpdateTask(
          activeColumnKey,
          editingTaskId,
          { ...existing, ...formData },
          { completed: existing.completed, completedAt: existing.completedAt }
        );

        setColumns((prev) => {
          const newCols = { ...prev };
          const col = newCols[activeColumnKey];
          newCols[activeColumnKey] = {
            ...col,
            items: col.items.map((item) =>
              item.id === editingTaskId ? updatedTask : item
            ),
          };
          return newCols;
        });
      } else {
        const newTask = await apiCreateTask(activeColumnKey, formData);

        setColumns((prev) => {
          const col = prev[activeColumnKey];
          const { incomplete, completed } = getTasksByStatus(col.items);
          return {
            ...prev,
            [activeColumnKey]: {
              ...col,
              items: [newTask, ...incomplete, ...completed],
            },
          };
        });
      }

      setIsModalOpen(false);
      setEditingTaskId(null);
    } catch (err) {
      console.error(err);
      alert('Failed to save task');
    }
  };

  const toggleCompleted = async (e, columnKey, taskId) => {
    e.stopPropagation();
    e.preventDefault();
    if (dragState.isDragging) return;

    const column = columns[columnKey];
    const task = column.items.find((t) => t.id === taskId);
    if (!task) return;

    const newCompleted = !task.completed;
    const overrides = {
      completed: newCompleted,
      completedAt: newCompleted
        ? new Date().toISOString().slice(0, 19).replace('T', ' ')
        : null,
    };

    try {
      const updatedTask = await apiUpdateTask(columnKey, taskId, task, overrides);

      setColumns((prev) => {
        const newCols = { ...prev };
        const col = newCols[columnKey];
        const updatedItems = col.items.map((item) =>
          item.id === taskId ? updatedTask : item
        );
        const incomplete = updatedItems.filter((i) => !i.completed);
        const completed = updatedItems.filter((i) => i.completed);

        newCols[columnKey] = {
          ...col,
          items: [...incomplete, ...completed],
        };
        return newCols;
      });
    } catch (err) {
      console.error(err);
      alert('Failed to update task status');
    }
  };
  const renderTaskCard = (item, columnKey, index, isCompleted = false) => (
    <div
      key={item.id}
      className={`task-board-card ${isCompleted ? 'task-board-card-completed task-board-card-condensed' : ''}`}
      onMouseDown={!isCompleted ? (e) => handleMouseDown(e, columnKey, item, index) : undefined}
      onClick={isCompleted ? () => handleEditClick(columnKey, item) : undefined}
    >
      {isCompleted ? (
        <>
          <div className="task-board-card-condensed-content">
            <CheckCircleIcon completed={true} />
            {item.imagePreview && (
              <img
                src={item.imagePreview}
                alt={item.title}
                className="task-board-card-condensed-image"
                onClick={(e) => {
                  e.stopPropagation();                             // don't trigger edit
                  openImageViewer(item.imagePreview, item.title);  // open zoom
                }}
              />
            )}
            <span className="task-board-card-condensed-title">{item.title}</span>
            <span className="task-board-card-condensed-qty">Qty: {item.quantity}</span>
          </div>
          <button
            className="task-board-complete-btn task-board-undo-btn"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => toggleCompleted(e, columnKey, item.id)}
            title="Mark as Incomplete"
          >
            ↩
          </button>
        </>
      ) : (
        <>
          <div className="task-board-card-badge">Qty: {item.quantity}</div>
          {/* NEW: image preview in card */}

          {
            item.imagePreview && (
              <div
                className="task-board-card-image-wrapper"
                onMouseDown={(e) => e.stopPropagation()}           // prevent drag start
                onClick={(e) => {
                  e.stopPropagation();                             // prevent card click
                  openImageViewer(item.imagePreview, item.title);  // open zoom
                }}
              >
                <img
                  src={item.imagePreview}
                  alt={item.title}
                  className="task-board-card-image"
                />
              </div>
            )
          }


          <h4 className="task-board-card-title">{item.title}</h4>

          {item.note && (
            <div className="task-board-card-note">
              <NoteIcon />
              <span className="task-board-note-text">{item.note}</span>
            </div>
          )}

          <div className="task-board-card-dates">
            <CalendarIcon />
            <span>{item.startDate || 'No Date'} - {item.endDate || 'No Date'}</span>
          </div>

          <div className="task-board-card-footer">
            <div className="task-board-assignee">
              <div className="task-board-avatar">
                {item.assignee ? item.assignee.charAt(0).toUpperCase() : '?'}
              </div>
              <span className="task-board-assignee-name">{item.assignee || 'Unassigned'}</span>
            </div>

            <button
              className="task-board-complete-btn"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => toggleCompleted(e, columnKey, item.id)}
              title="Mark as Completed"
            >
              <CheckCircleIcon completed={false} />
            </button>
          </div>
        </>
      )}
    </div>
  );

  const maxCompleted = useMemo(() => {
    return Math.max(...filteredStats.map(a => a.totalCompleted), 1);
  }, [filteredStats]);

  return (
    <div className="task-board-container" ref={containerRef}>
      {/* Header */}
      <header className="task-board-header">
        <div className="task-board-titles">
          <h1>Manage Operation</h1>
          <div className="task-board-breadcrumbs">
            <span>Dashboard</span>
            <span className="task-board-chevron">&gt;</span>
            <span className="task-board-current">Operations</span>
          </div>
        </div>

        <div className="task-board-controls">
          <div className="task-board-search-bar">
            <span className="task-board-search-icon"><SearchIcon /></span>
            <input
              type="text"
              placeholder="         Search task..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Right side buttons group - Filter & Stats together */}
          <div className="task-board-controls-right">
            {/* Filter Button */}
            <div className="task-board-filter-container" ref={filterRef}>
              <button
                className={`task-board-filter-btn ${dateFilter.isActive ? 'active' : ''}`}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                title="Filter by Date"
              >
                <FilterIcon />
                <span>Filter</span>
                {dateFilter.isActive && (
                  <span className="task-board-filter-badge">1</span>
                )}
              </button>

              {/* Filter Dropdown */}
              {isFilterOpen && (
                <div className="task-board-filter-dropdown">
                  <div className="task-board-filter-header">
                    <h3>Filter by Start Date</h3>
                  </div>
                  <div className="task-board-filter-body">
                    <div className="task-board-filter-group">
                      <label>From Date</label>
                      <input
                        type="date"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="task-board-filter-group">
                      <label>To Date</label>
                      <input
                        type="date"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="task-board-filter-footer">
                    <button
                      className="task-board-filter-clear-btn"
                      onClick={handleClearFilter}
                    >
                      Clear
                    </button>
                    <button
                      className="task-board-filter-apply-btn"
                      onClick={handleApplyFilter}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Stats Button */}
            <button
              className="task-board-stats-btn"
              onClick={() => setIsStatsOpen(true)}
              title="View Completion Stats"
            >
              <StatsIcon />
              <span>Stats</span>
              {totalStats.total > 0 && (
                <span className="task-board-stats-badge">{totalStats.total}</span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Active Indicator */}
        {dateFilter.isActive && (
          <div className="task-board-filter-active-indicator">
            <span>Filtered: {dateFilter.startDate || 'Any'} to {dateFilter.endDate || 'Any'}</span>
            <button onClick={handleClearFilter}>Clear Filter</button>
          </div>
        )}
      </header>

      {/* Board Area - Use filteredColumns instead of columns */}
      {/* Board Area - Use searchFilteredColumns instead of filteredColumns */}
      <div className="task-board-columns-container">
        {Object.entries(searchFilteredColumns).length === 0 && searchQuery.trim() ? (
          <div className="task-board-no-results">
            <span>No tasks found for "</span>
            <span className="task-board-no-results-query">
              {searchQuery.trim()}
            </span>
            <span>"</span>
          </div>
        ) : (
          Object.entries(searchFilteredColumns).map(([key, column]) => {
            const { incomplete, completed } = getTasksByStatus(column.items);
            const isExpanded = expandedCompleted[key];

            return (
              <div
                className="task-board-column"
                key={key}
                data-column-key={key}
              >
                <div className="task-board-column-header">
                  <div className="task-board-column-header-left">
                    <span
                      className="task-board-dot"
                      style={{ backgroundColor: column.color }}
                    ></span>
                    <h3>
                      {column.title}
                      <span className="task-board-count">
                        ({incomplete.length})
                      </span>
                    </h3>
                  </div>
                  <button
                    className="task-board-add-btn"
                    onClick={() => handleAddClick(key)}
                  >
                    <PlusIcon />
                  </button>
                </div>

                <div className="task-board-column-content">
                  <div className="task-board-incomplete-section">
                    {incomplete.map((item, index) =>
                      renderTaskCard(item, key, index, false)
                    )}

                    {incomplete.length === 0 && !dragState.isDragging && (
                      <div className="task-board-empty-state">
                        {dateFilter.isActive || searchQuery.trim()
                          ? 'No tasks match filter'
                          : 'No active tasks'}
                      </div>
                    )}
                  </div>

                  {completed.length > 0 && (
                    <div className="task-board-completed-section">
                      <div
                        className="task-board-completed-header"
                        onClick={() => toggleCompletedSection(key)}
                      >
                        <div className="task-board-completed-header-left">
                          <ChevronDownIcon isOpen={isExpanded} />
                          <span>Completed</span>
                          <span className="task-board-completed-count">
                            {completed.length}
                          </span>
                        </div>
                        <button
                          className="task-board-clear-completed-btn"
                          onClick={(e) => clearCompletedTasks(e, key)}
                          title="Clear all completed tasks"
                        >
                          <TrashIcon />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="task-board-completed-list">
                          {completed.map((item, index) =>
                            renderTaskCard(item, key, index, true)
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats Panel */}
      {isStatsOpen && (
        <div className="task-board-stats-overlay">
          <div className="task-board-stats-panel">
            <div className="task-board-stats-header">
              {selectedAssignee ? (
                <>
                  <button
                    className="task-board-back-btn"
                    onClick={() => setSelectedAssignee(null)}
                  >
                    <BackIcon />
                  </button>
                  <h2>{selectedAssignee.name}'s Completed Tasks</h2>
                </>
              ) : (
                <h2>Completion Stats by Assignee</h2>
              )}
              <button
                className="task-board-close-btn"
                onClick={() => {
                  setIsStatsOpen(false);
                  setSelectedAssignee(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {!selectedAssignee && (
              <div className="task-board-stats-actions">
                <div className="task-board-stats-filter">
                  <label>Filter by Column:</label>
                  <select
                    value={statsFilter}
                    onChange={(e) => setStatsFilter(e.target.value)}
                  >
                    <option value="all">All Columns</option>
                    {Object.entries(columns).map(([key, col]) => (
                      <option key={key} value={key}>{col.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {!selectedAssignee && (
              <div className="task-board-stats-summary">
                <div className="task-board-summary-card">
                  <span className="task-board-summary-value">{totalStats.total}</span>
                  <span className="task-board-summary-label">Tasks Completed</span>
                </div>
                <div className="task-board-summary-card">
                  <span className="task-board-summary-value">{totalStats.totalQty}</span>
                  <span className="task-board-summary-label">Total Quantity</span>
                </div>
                <div className="task-board-summary-card">
                  <span className="task-board-summary-value">{filteredStats.length}</span>
                  <span className="task-board-summary-label">Assignees</span>
                </div>
              </div>
            )}

            <div className="task-board-stats-content">
              {selectedAssignee ? (
                <div className="task-board-assignee-detail">
                  <div className="task-board-assignee-detail-header">
                    <div className="task-board-detail-avatar">
                      {selectedAssignee.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="task-board-detail-info">
                      <h3>{selectedAssignee.name}</h3>
                      <p>{selectedAssignee.totalCompleted} tasks completed • {selectedAssignee.totalQuantity} total quantity</p>
                    </div>
                  </div>

                  <div className="task-board-detail-tasks">
                    <h4>Completed Tasks</h4>
                    {selectedAssignee.tasks.map((task, idx) => (
                      <div key={idx} className="task-board-detail-task-item">
                        <div className="task-board-detail-task-main">
                          <CheckCircleIcon completed={true} />
                          <div className="task-board-detail-task-info">
                            <span className="task-board-detail-task-title">{task.title}</span>
                            <span className="task-board-detail-task-meta">
                              {task.columnTitle} • Qty: {task.quantity}
                            </span>
                          </div>
                        </div>
                        {task.endDate && (
                          <span className="task-board-detail-task-date">
                            <CalendarIcon /> {task.endDate}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {filteredStats.length === 0 ? (
                    <div className="task-board-stats-empty">
                      <p>No completed tasks found</p>
                      <span>Complete some tasks to see stats here</span>
                    </div>
                  ) : (
                    <div className="task-board-assignee-list">
                      {filteredStats.map((assignee, idx) => (
                        <div
                          key={idx}
                          className="task-board-assignee-row"
                          onClick={() => setSelectedAssignee(assignee)}
                        >
                          <div className="task-board-assignee-row-left">
                            <div className="task-board-assignee-row-avatar">
                              {assignee.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="task-board-assignee-row-info">
                              <span className="task-board-assignee-row-name">
                                {assignee.name}
                              </span>
                              <span className="task-board-assignee-row-meta">
                                {assignee.totalCompleted} tasks • {assignee.totalQuantity} qty
                              </span>
                            </div>
                          </div>

                          <div className="task-board-assignee-row-right">
                            <div className="task-board-progress-bar-container">
                              <div
                                className="task-board-progress-bar-fill"
                                style={{
                                  width: `${(assignee.totalCompleted / maxCompleted) * 100}%`
                                }}
                              ></div>
                            </div>
                            <span className="task-board-assignee-row-count">
                              {assignee.totalCompleted}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {isModalOpen && (
        <div className="task-board-modal-overlay">
          <div className="task-board-modal-content">
            <div className="task-board-modal-header">
              <h2>{editingTaskId ? 'Edit Task' : 'Add New Task'}</h2>
              <button className="task-board-close-btn" onClick={() => setIsModalOpen(false)}>&times;</button>
            </div>

            <div className="task-board-modal-body">
              <form onSubmit={handleSave}>
                <div className="task-board-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. UI/UX Design"
                    required
                  />
                </div>
                <div className="task-board-form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    placeholder="e.g. 100"
                  />
                </div>
                <div className="task-board-form-group">
                  <label>Assignee Name</label>
                  <input
                    type="text"
                    name="assignee"
                    value={formData.assignee}
                    onChange={handleInputChange}
                    placeholder="e.g. John Doe"
                  />
                </div>

                <div className="task-board-form-group">
                  <label>Reference Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />

                  {formData.imagePreview && (
                    <div className="task-board-image-preview">
                      <img src={formData.imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="task-board-image-remove-btn"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            imageFile: null,
                            imagePreview: '',
                          }))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div className="task-board-form-group">
                  <label>Note / Remarks</label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    placeholder="Add any additional notes, instructions, or remarks..."
                    rows={3}
                  />
                </div>

                <div className="task-board-form-row">
                  <div className="task-board-form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="task-board-form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="task-board-modal-footer">
                  <button type="button" className="task-board-cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                  <button type="submit" className="task-board-save-btn">
                    {editingTaskId ? 'Update Task' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

            {/* Image Viewer (Zoom) */}
      {imageViewer.isOpen && (
        <div
          className="task-board-image-viewer-overlay"
          onClick={closeImageViewer}
        >
          <div
            className="task-board-image-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="task-board-image-viewer-close"
              type="button"
              onClick={closeImageViewer}
            >
              <CloseIcon />
            </button>

            {imageViewer.title && (
              <div className="task-board-image-viewer-title">
                {imageViewer.title}
              </div>
            )}

            <img
              src={imageViewer.src}
              alt={imageViewer.title || 'Task image'}
              className="task-board-image-viewer-img"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Operations;